// Competition insights for managers (namat#35): «ايش اكثر سلوك تمت المواضبة
// عليه، وأقل أو أصعب سلوك… أطول فترة التزام متوالي… صاحب اكثر تنوع».
//
// Pure and shared: this file is copied byte for byte between web
// (src/lib/competitions/competitionInsights.ts) and mobile (same path). The
// query layer reads the competition's logs once (the same read as the
// standings) and hands them here.
//
// Rules:
// - Only completed logs inside the competition's CURRENT start..end days count
//   (isLogInCompetitionWindow, namat#34), and only from `participantIds` (the
//   active beneficiaries the standings use, namat#87).
// - A (user, habit, day) is counted once, even if a log were duplicated.
// - Habit commitment = completions of the habit ÷ (participants × days so far).
//   Every habit shares that denominator, so the ranking is the same as by
//   completions; the rate only makes the number readable.
// - Streak = the longest run of consecutive calendar days on which the user
//   completed at least one habit. Variety = how many different competition
//   habits the user completed at least once.
// - Ties: higher value wins, then name, then user/habit id — deterministic on
//   every platform (plain string order, no locale collation).
import { isLogInCompetitionWindow, riyadhDateKey, type CompetitionWindow } from './competitionDates';

const DAY_MS = 24 * 60 * 60 * 1000;

export interface InsightLog {
  userId?: string;
  habitId?: string;
  /** `yyyy-MM-dd`, the day the habit was completed. */
  date?: string;
  completed?: boolean;
}

export interface InsightHabit {
  id: string;
  name: string;
}

export interface HabitInsight {
  habitId: string;
  name: string;
  /** Completed (user, day) pairs for this habit inside the window. */
  completed: number;
  /** completed ÷ (participants × elapsed days), 0..1. */
  rate: number;
  /** How many other habits have exactly the same count. */
  ties: number;
}

export interface PersonInsight {
  userId: string;
  name: string;
  /** Days for the streak, distinct habits for the variety. */
  value: number;
  /** How many other participants share exactly this value. */
  ties: number;
}

export interface CompetitionInsights {
  /** null when no habit was completed yet. */
  mostKeptHabit: HabitInsight | null;
  /** null with fewer than two habits, or when every habit has the same count. */
  leastKeptHabit: HabitInsight | null;
  longestStreak: PersonInsight | null;
  mostVariety: PersonInsight | null;
  /** Competition days elapsed so far (today included), capped at the end day. */
  elapsedDays: number;
  participants: number;
}

const dayNumber = (key: string): number => Math.round(Date.parse(`${key}T00:00:00Z`) / DAY_MS);

const byText = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

/** The longest run of consecutive calendar days among `yyyy-MM-dd` keys. */
export function longestDayRun(dateKeys: Iterable<string>): number {
  const days = Array.from(new Set(Array.from(dateKeys).map(dayNumber)))
    .filter((d) => Number.isFinite(d))
    .sort((a, b) => a - b);
  let best = 0;
  let run = 0;
  for (let i = 0; i < days.length; i++) {
    run = i > 0 && days[i] === days[i - 1] + 1 ? run + 1 : 1;
    if (run > best) best = run;
  }
  return best;
}

/** Days of the competition elapsed at `now` (Riyadh days), 0 before it starts. */
export function elapsedCompetitionDays(window: CompetitionWindow, now: Date): number {
  const start = dayNumber(riyadhDateKey(window.startDate));
  const end = dayNumber(riyadhDateKey(window.endDate));
  const today = Math.min(dayNumber(riyadhDateKey(now)), end);
  return Math.max(0, today - start + 1);
}

function topPerson(values: Map<string, number>, nameOf: (userId: string) => string): PersonInsight | null {
  const rows = Array.from(values.entries())
    .filter(([, value]) => value > 0)
    .map(([userId, value]) => ({ userId, name: nameOf(userId), value }));
  if (rows.length === 0) return null;
  rows.sort((a, b) => b.value - a.value || byText(a.name, b.name) || byText(a.userId, b.userId));
  const best = rows[0];
  return { ...best, ties: rows.filter((r) => r.value === best.value).length - 1 };
}

export function competitionInsights(input: {
  logs: InsightLog[];
  habits: InsightHabit[];
  /** Everyone taking part (zero rows included), as in the standings. */
  participantIds: string[];
  /** Display names; a missing name falls back to the user id. */
  names?: Map<string, string>;
  window: CompetitionWindow;
  now: Date;
}): CompetitionInsights {
  const { logs, habits, participantIds, names, window, now } = input;
  const participants = new Set(participantIds.filter(Boolean));
  const habitIds = new Set(habits.map((h) => h.id));
  const nameOf = (userId: string) => names?.get(userId) || userId;

  const seen = new Set<string>();
  const perHabit = new Map<string, number>();
  const daysOf = new Map<string, Set<string>>();
  const habitsOf = new Map<string, Set<string>>();

  for (const log of logs) {
    if (log.completed !== true || !log.userId || !participants.has(log.userId)) continue;
    if (!isLogInCompetitionWindow(log.date, window)) continue;
    const key = `${log.userId}|${log.habitId ?? ''}|${log.date ?? ''}`;
    if (seen.has(key)) continue;
    seen.add(key);

    if (log.date) {
      const days = daysOf.get(log.userId) ?? new Set<string>();
      days.add(log.date);
      daysOf.set(log.userId, days);
    }
    if (log.habitId && habitIds.has(log.habitId)) {
      perHabit.set(log.habitId, (perHabit.get(log.habitId) || 0) + 1);
      const kinds = habitsOf.get(log.userId) ?? new Set<string>();
      kinds.add(log.habitId);
      habitsOf.set(log.userId, kinds);
    }
  }

  const elapsedDays = elapsedCompetitionDays(window, now);
  const capacity = participants.size * elapsedDays;
  const habitRows = habits.map((h) => {
    const completed = perHabit.get(h.id) || 0;
    return { habitId: h.id, name: h.name, completed, rate: capacity > 0 ? Math.min(1, completed / capacity) : 0 };
  });
  const tiesOf = (completed: number) => habitRows.filter((r) => r.completed === completed).length - 1;

  const most = [...habitRows].sort((a, b) => b.completed - a.completed || byText(a.name, b.name) || byText(a.habitId, b.habitId))[0];
  const least = [...habitRows].sort((a, b) => a.completed - b.completed || byText(a.name, b.name) || byText(a.habitId, b.habitId))[0];
  const mostKeptHabit = most && most.completed > 0 ? { ...most, ties: tiesOf(most.completed) } : null;
  const leastKeptHabit =
    mostKeptHabit && least && habitRows.length >= 2 && least.completed < mostKeptHabit.completed
      ? { ...least, ties: tiesOf(least.completed) }
      : null;

  const streaks = new Map(Array.from(daysOf.entries()).map(([userId, days]) => [userId, longestDayRun(days)]));
  const variety = new Map(Array.from(habitsOf.entries()).map(([userId, kinds]) => [userId, kinds.size]));

  return {
    mostKeptHabit,
    leastKeptHabit,
    longestStreak: topPerson(streaks, nameOf),
    mostVariety: topPerson(variety, nameOf),
    elapsedDays,
    participants: participants.size,
  };
}
